/**
 * Which alert the map should open by itself.
 *
 * Extracted from the page because the bug it fixes is subtle and has bitten
 * twice: the bell and the map feed have DIFFERENT scopes. The bell is fed from
 * the Queue plus the recent-arrival probe, neither bounded by the global date
 * picker; the map feed is strictly inside it. Selecting the auto-load candidate
 * by intersecting the two silently dropped every arrival outside the operator's
 * chosen window, and the map quietly stopped opening new alerts at all.
 *
 * So the bell's set is authoritative for WHICH alert is new, and the feed is
 * consulted only to enrich it.
 */
import type { Alert } from "@/alerts/types/machine-alert";
import type { AlertNotification } from "@/alerts/stores/use-alert-notify-store";

/**
 * The only fields opening an alert actually needs. Narrower than `Alert` so an
 * alert known only to the bell can still be opened; the detail card fetches
 * whatever else it renders.
 */
export type AlertFocusTarget = Pick<Alert, "id" | "timestamp" | "device_mxid"> &
  Partial<Pick<Alert, "gps">>;

/**
 * The newest genuinely-new alert, or null.
 *
 * `newAlertIds` already excludes backlog and anything resolved or aged out, so
 * membership is the whole test. The feed row is preferred when there is one
 * because it carries the GPS the camera move needs — without it the card still
 * opens, the map just doesn't pan.
 */
export const pickNewestNewAlert = (
  notifications: AlertNotification[],
  newAlertIds: Set<number>,
  feed: Alert[],
): AlertFocusTarget | null => {
  const candidates = notifications.filter((a) => newAlertIds.has(a.id));
  if (candidates.length === 0) return null;

  const newest = candidates.reduce((a, b) =>
    new Date(b.timestamp).getTime() > new Date(a.timestamp).getTime() ? b : a,
  );

  return (
    feed.find((a) => a.id === newest.id) ?? {
      id: newest.id,
      timestamp: newest.timestamp,
      device_mxid: newest.deviceMxid,
    }
  );
};

/**
 * The alerts the card's Prev/Next step through, newest first.
 *
 * The union of the map feed and the bell, not just the feed. The feed obeys the
 * global date picker, so navigating it alone meant an operator sent to an alert
 * from outside that window had nowhere to go — and with a narrow window the
 * buttons vanished entirely, because there was fewer than two of anything to
 * step between. The bell's alerts are equally real work; they simply live
 * outside the review range.
 *
 * Feed rows win on collision: they carry the GPS the map camera needs.
 */
export const buildAlertNavList = (
  feed: Alert[],
  notifications: AlertNotification[],
): AlertFocusTarget[] => {
  const byId = new Map<number, AlertFocusTarget>();

  for (const item of notifications) {
    byId.set(item.id, {
      id: item.id,
      timestamp: item.timestamp,
      device_mxid: item.deviceMxid,
    });
  }
  // Second, so a feed row replaces the bell's thinner record.
  for (const alert of feed) byId.set(alert.id, alert);

  return [...byId.values()].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
};

/**
 * Where an alert sits in {@link buildAlertNavList}.
 *
 * An alert that is in the list returns its own slot. One that is not — opened
 * from a timeline segment, say — returns the slot it WOULD occupy, so stepping
 * lands on the alerts either side of it in time rather than dead-ending.
 */
export const navAnchorIndex = (
  navList: AlertFocusTarget[],
  selectedAlertId: string | null,
  selectedTimestamp: string | null,
): number => {
  const own = navList.findIndex((a) => String(a.id) === selectedAlertId);
  if (own >= 0) return own;
  if (!selectedTimestamp) return -1;

  const at = new Date(selectedTimestamp).getTime();
  const older = navList.findIndex((a) => new Date(a.timestamp).getTime() <= at);
  return older < 0 ? navList.length : older;
};
